/*
  rbc-gallery-collezioni.js — the collezioni picker (flagship hub template). Port of the lite prototype's Tops().
  The server renders everything (stage photograph, swatches as links to their collections, caption, all panels);
  this upgrades the swatches to a radiogroup and runs the stage:
  - hover (fine pointer) or keyboard focus previews: the caption greys to the other collezione and, between two
    registered photographs of the same boot, its top wipes 40% of the way down;
  - click / Enter / Space commits: the wipe runs to the bottom (or, to a different boot, the new photograph
    crossfades in), the caption and the pieces swap. On touch there is no hover: a tap commits.
  - reduced motion: every swap is instant.
  Stage photographs other than the chosen one are fetched on first interaction; a panel's thumbnails stay lazy
  until its collezione is previewed or chosen. The state is the section's own (not the wall's).
*/
(() => {
  'use strict';
  const RM = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine = () => matchMedia('(hover: hover) and (pointer: fine)').matches;
  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
  const easeOut = t => 1 - Math.pow(1 - t, 3);
  const easeIO = t => t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  const settle = img => (img.decode ? img.decode() : Promise.resolve()).catch(() => {}); // resolves once the photograph can paint (or failed)

  function init(root) {
    if (root.rbcGc) return;
    const stage = root.querySelector('[data-gc-stage]'), drawer = root.querySelector('[data-gc-drawer]'), now = root.querySelector('[data-gc-now]');
    const base = stage && stage.querySelector('.rbc-gc-base img'), overLyr = stage && stage.querySelector('.rbc-gc-over');
    const over = overLyr && overLyr.querySelector('img');
    const sws = drawer ? Array.from(drawer.querySelectorAll('.rbc-gc-sw')) : [], panels = Array.from(root.querySelectorAll('.rbc-gc-panel'));
    if (!base || !over || !now || sws.length < 2 || panels.length !== sws.length) return;
    root.rbcGc = true;

    const baseFr = base.parentElement, overFr = over.parentElement;
    const nameEl = now.querySelector('[data-gc-name]'), lineEl = now.querySelector('[data-gc-line]');
    const sizes = base.getAttribute('sizes') || '';
    const D = sws.map(a => ({ name: a.dataset.name, line: a.dataset.line, src: a.dataset.src, srcset: a.dataset.srcset, alt: a.dataset.alt, s: a.dataset.s || 100, x: a.dataset.x || 0, y: a.dataset.y || 0, reg: a.hasAttribute('data-reg') }));
    let cur = Math.max(0, sws.findIndex(a => a.getAttribute('aria-current') === 'true'));
    let pvI = null, want = null, p = -5, raf = 0, busy = false, loaded = false;

    // the links become a radiogroup (roving tabindex; the collection link stays for modified clicks)
    const h = root.querySelector('.rbc-gc-h');
    drawer.setAttribute('role', 'radiogroup');
    if (h && h.id) drawer.setAttribute('aria-labelledby', h.id);
    sws.forEach((a, i) => { a.setAttribute('role', 'radio'); a.removeAttribute('aria-current'); a.setAttribute('aria-checked', i === cur); a.tabIndex = i === cur ? 0 : -1; a.draggable = false; });

    const place = (fr, d) => { fr.style.setProperty('--s', d.s); fr.style.setProperty('--tx', d.x); fr.style.setProperty('--ty', d.y); };
    const put = (img, d) => { img.sizes = sizes; if (d.srcset) img.srcset = d.srcset; img.src = d.src; };
    const setP = v => { p = v; stage.style.setProperty('--p', v.toFixed(2) + '%'); };
    const sameBoot = (x, y) => D[x].reg && D[y].reg; // the wipe only makes sense between two photographs of the same boot
    const show = (i, pv) => { nameEl.textContent = D[i].name; lineEl.textContent = D[i].line; now.classList.toggle('is-pv', !!pv); };
    const warm = i => panels[i].querySelectorAll('img[loading="lazy"]').forEach(im => { im.loading = 'eager'; });
    const preload = () => {
      if (loaded) return; loaded = true; base.loading = 'eager';
      D.forEach(d => { const im = new Image(); im.sizes = sizes; if (d.srcset) im.srcset = d.srcset; im.src = d.src; });
    };
    function tween(to, dur, ez, done) {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      if (RM()) { setP(to); done && done(); return; }
      const p0 = p, t0 = performance.now();
      const step = t => { const k = clamp((t - t0) / dur); setP(p0 + (to - p0) * ez(k)); if (k < 1) raf = requestAnimationFrame(step); else { raf = 0; done && done(); } };
      raf = requestAnimationFrame(step);
    }
    function setOver(i) { if (pvI === i) return; pvI = i; put(over, D[i]); place(overFr, D[i]); setP(-5); }
    function preview(i) {
      if (busy || i === cur) { unpreview(); return; }
      want = i; warm(i); show(i, true);
      if (!sameBoot(cur, i)) return;
      setOver(i);
      settle(over).then(() => { if (want === i && !busy) tween(40, 420, easeOut); }); // never wipe in paper while the photograph is still on its way
    }
    function unpreview() {
      want = null;
      if (busy) return;
      show(cur);
      if (pvI == null) return;
      tween(-5, 320, t => t * t, () => { if (!busy) pvI = null; });
    }
    function swapPanel(i) {
      const from = panels[cur], to = panels[i];
      const reveal = () => { from.hidden = true; from.classList.remove('is-out'); to.hidden = false; const strip = to.querySelector('.rbc-gc-pieces'); if (strip) strip.scrollLeft = 0; };
      if (RM()) { reveal(); return; }
      from.classList.add('is-out');
      setTimeout(() => {
        reveal();
        to.querySelectorAll('.rbc-gc-pc, .rbc-gc-view').forEach((c, k) => c.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 480, delay: 60 + k * 30, easing: 'cubic-bezier(.33,0,0,1)', fill: 'backwards' }));
      }, 160);
    }
    function commit(i) {
      if (busy || i === cur) return;
      busy = true; want = null; preload(); warm(i);
      const d = D[i], wipe = sameBoot(cur, i);
      if (wipe) setOver(i); else { pvI = i; put(over, d); place(overFr, d); }
      sws.forEach((s, k) => { s.setAttribute('aria-checked', k === i); s.tabIndex = k === i ? 0 : -1; });
      if (RM()) show(i); else { now.classList.add('is-out'); setTimeout(() => { show(i); now.classList.remove('is-out'); }, 220); }
      swapPanel(i);
      const land = () => {
        base.sizes = sizes; if (over.srcset) base.srcset = over.srcset; base.src = over.src; base.alt = d.alt; place(baseFr, d);
        const fin = () => { setP(-5); overLyr.style.opacity = ''; pvI = null; cur = i; busy = false; };
        settle(base).then(fin);
      };
      const go = () => {
        if (wipe) { tween(106, Math.max(380, 720 * (106 - p) / 111), easeIO, land); return; }
        setP(110); // a different boot: no shared outline to wipe along, so the new photograph simply develops over the old
        if (RM()) { land(); return; }
        overLyr.style.opacity = 0;
        overLyr.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 620, easing: 'cubic-bezier(.33,0,0,1)' }).finished.then(() => { overLyr.style.opacity = 1; land(); }, land);
      };
      settle(over).then(go);
    }

    over.sizes = sizes;
    sws.forEach((s, i) => {
      s.addEventListener('pointerenter', e => { preload(); if (e.pointerType === 'mouse' && fine()) preview(i); });
      s.addEventListener('pointerleave', e => { if (e.pointerType === 'mouse') unpreview(); });
      s.addEventListener('click', e => { if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button) return; e.preventDefault(); commit(i); });
      s.addEventListener('focus', () => { preload(); if (s.matches(':focus-visible')) preview(i); });
      s.addEventListener('blur', () => unpreview());
      s.addEventListener('keydown', e => {
        if (e.key === ' ') { e.preventDefault(); commit(i); return; }
        const dir = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
        if (!dir) return;
        e.preventDefault(); sws[(i + dir + sws.length) % sws.length].focus();
      });
    });
    // theme editor: selecting a block shows its collezione
    root.addEventListener('shopify:block:select', e => { const i = sws.findIndex(s => s.dataset.blockId === (e.detail && e.detail.blockId)); if (i >= 0) commit(i); });
  }

  const boot = scope => (scope || document).querySelectorAll('.rbc-g-collezioni').forEach(init);
  boot();
  document.addEventListener('shopify:section:load', e => boot(e.target));
})();
